import React from 'react';
import { useAppContext } from '../../context/AppContext';
import { GraduationCap, Users, Lightbulb, Target, ArrowRight, Clock, Award } from 'lucide-react';

const InternshipProgramPage = () => {
    const { setContactFormOpen } = useAppContext();

    const scrollToProgramGoals = () => {
        const programGoalsSection = document.getElementById('program-goals-section');
        if (programGoalsSection) {
            programGoalsSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    return (
        <div className="flex flex-col">
            {/* Hero Section */}
            <section className="relative bg-gradient-to-r from-indigo-900 via-purple-800 to-pink-700 text-white py-20 md:py-32">
                <div className="container mx-auto px-4 relative">
                    <div className="max-w-4xl">
                        <div className="mb-6">
                            <span className="inline-block bg-yellow-400 text-black px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wide">
                                Coming Soon
                            </span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                            EXCESSD Internship Program
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-90">
                            At EXCESSD, we believe that nurturing young talent is key to building a strong, future-ready technology ecosystem in India.
                        </p>
                        <p className="text-lg mb-12 leading-relaxed opacity-80">
                            While we are in the early stages of our journey, one of our core aspirations is to create meaningful pathways for students and recent graduates to grow into world-class software professionals.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button 
                                onClick={() => setContactFormOpen(true)}
                                className="bg-yellow-400 text-black font-bold py-4 px-8 rounded-md hover:bg-yellow-300 transition-colors inline-flex items-center justify-center group"
                            >
                                EXPRESS INTEREST
                                {/* <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" /> */}
                            </button>
                            <button 
                                onClick={scrollToProgramGoals}
                                className="border-2 border-white text-white font-bold py-4 px-8 rounded-md hover:bg-white hover:text-indigo-900 transition-colors"
                            >
                                Learn More
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Program Vision Section */}
            <section className="py-20">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Vision for Internships</h2>
                            <p className="text-lg mb-6 text-gray-700">
                                Inspired by successful models across the globe, our upcoming Student Internship Program aims to bridge the gap between academic learning and real-world software engineering.
                            </p>
                            <p className="text-lg mb-8 text-gray-700">
                                We envision a hands-on, mentorship-driven environment where interns can work on practical challenges, learn from experienced professionals, and gain exposure to the culture and pace of modern product development.
                            </p>

                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <div className="bg-blue-100 p-2 rounded-full mr-4 mt-1">
                                        <Target className="text-blue-600" size={16} />
                                    </div>
                                    <span className="text-gray-700">Bridge academic learning with industry practice</span>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-green-100 p-2 rounded-full mr-4 mt-1">
                                        <Users className="text-green-600" size={16} />
                                    </div>
                                    <span className="text-gray-700">Mentorship-driven learning environment</span>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-purple-100 p-2 rounded-full mr-4 mt-1">
                                        <Lightbulb className="text-purple-600" size={16} />
                                    </div>
                                    <span className="text-gray-700">Hands-on experience with real projects</span>
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="relative rounded-lg h-96 overflow-hidden shadow-lg">
                                <img 
                                    src="/assets/images/internship-program.jpg" 
                                    alt="Internship Program" 
                                    className="w-full h-full object-cover"
                                />
                                {/* <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
                                    <div className="text-white p-6 w-full">
                                        <p className="text-xl font-bold mb-2">Nurturing Future Talent</p>
                                        <p className="text-sm opacity-90">Building Tomorrow's Engineers</p>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Program Goals Section */}
            <section id="program-goals-section" className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Program Goals</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Our goal is to blend academic foundations with industry-ready practices, enabling interns to gain comprehensive skills and insights.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white p-8 rounded-xl shadow-sm">
                            <div className="flex items-start mb-6">
                                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                                    <span className="text-2xl">🛠️</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold mb-3">Technical Skills Development</h3>
                                    <p className="text-gray-700 mb-4">
                                        Gain hands-on experience with modern technologies, development frameworks, and industry-standard tools used in professional software development.
                                    </p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Modern programming languages and frameworks</li>
                                        <li>• Version control and collaboration tools</li>
                                        <li>• Testing and quality assurance practices</li>
                                        <li>• DevOps and deployment processes</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-xl shadow-sm">
                            <div className="flex items-start mb-6">
                                <div className="bg-green-100 p-3 rounded-lg mr-4">
                                    <span className="text-2xl">🤝</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold mb-3">Professional Growth</h3>
                                    <p className="text-gray-700 mb-4">
                                        Develop essential soft skills and professional competencies through collaborative work, team dynamics, and mentorship.
                                    </p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Communication and presentation skills</li>
                                        <li>• Team collaboration and leadership</li>
                                        <li>• Problem-solving and critical thinking</li>
                                        <li>• Professional etiquette and work culture</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-xl shadow-sm">
                            <div className="flex items-start mb-6">
                                <div className="bg-purple-100 p-3 rounded-lg mr-4">
                                    <span className="text-2xl">🌍</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold mb-3">Global Exposure</h3>
                                    <p className="text-gray-700 mb-4">
                                        Work on projects for international clients and understand global software development practices and standards.
                                    </p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• International client interaction</li>
                                        <li>• Cross-cultural communication</li>
                                        <li>• Global software standards</li>
                                        <li>• Remote collaboration practices</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-xl shadow-sm">
                            <div className="flex items-start mb-6">
                                <div className="bg-orange-100 p-3 rounded-lg mr-4">
                                    <span className="text-2xl">🚀</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold mb-3">Career Preparation</h3>
                                    <p className="text-gray-700 mb-4">
                                        Prepare for a successful career transition from academia to the professional world with guidance and support.
                                    </p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Career counseling and guidance</li>
                                        <li>• Portfolio development support</li>
                                        <li>• Interview preparation and skills</li>
                                        <li>• Networking opportunities</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Program Structure Section */}
            <section className="py-20">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Planned Program Structure</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            A comprehensive 6-month program designed to provide maximum learning and growth opportunities.
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Clock className="text-blue-600" size={24} />
                                </div>
                                <h3 className="text-xl font-bold mb-4">Month 1-2: Foundation</h3>
                                <div className="bg-blue-50 p-6 rounded-lg">
                                    <ul className="text-gray-700 space-y-2 text-sm">
                                        <li>• Company culture and values orientation</li>
                                        <li>• Technology stack introduction</li>
                                        <li>• Basic project assignments</li>
                                        <li>• Mentor assignment and initial goals</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="text-center">
                                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Users className="text-green-600" size={24} />
                                </div>
                                <h3 className="text-xl font-bold mb-4">Month 3-4: Development</h3>
                                <div className="bg-green-50 p-6 rounded-lg">
                                    <ul className="text-gray-700 space-y-2 text-sm">
                                        <li>• Active project participation</li>
                                        <li>• Client interaction opportunities</li>
                                        <li>• Advanced skill development</li>
                                        <li>• Mid-term evaluation and feedback</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="text-center">
                                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Award className="text-purple-600" size={24} />
                                </div>
                                <h3 className="text-xl font-bold mb-4">Month 5-6: Mastery</h3>
                                <div className="bg-purple-50 p-6 rounded-lg">
                                    <ul className="text-gray-700 space-y-2 text-sm">
                                        <li>• Independent project leadership</li>
                                        <li>• Final project presentation</li>
                                        <li>• Career guidance and planning</li>
                                        <li>• Full-time opportunity evaluation</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Commitment Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Commitment</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Though we're just getting started, our commitment is clear: to invest in India's engineering talent and contribute to a stronger, more capable tech workforce.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="relative">
                            <div className="relative rounded-lg h-96 overflow-hidden shadow-lg">
                                <img 
                                    src="/assets/images/commitment-future.jpg" 
                                    alt="Building the Future" 
                                    className="w-full h-full object-cover object-center"
                                />
                                {/* <div className="absolute inset-0 bg-black bg-opacity-50 flex items-end">
                                    <div className="text-white p-6 w-full">
                                        <p className="text-xl font-bold mb-2">Building the Future</p>
                                        <p className="text-sm opacity-90">Investing in Tomorrow's Leaders</p>
                                    </div>
                                </div> */}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-2xl font-bold mb-6">What Sets Us Apart</h3>
                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="bg-blue-100 p-2 rounded-full mr-4 mt-1">
                                        <span className="text-blue-600 font-bold">💡</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold mb-2">Innovation-Focused Learning</h4>
                                        <p className="text-gray-700">Exposure to cutting-edge technologies and innovative problem-solving approaches.</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="bg-green-100 p-2 rounded-full mr-4 mt-1">
                                        <span className="text-green-600 font-bold">🎯</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold mb-2">Personalized Mentorship</h4>
                                        <p className="text-gray-700">One-on-one guidance from experienced professionals tailored to your growth needs.</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="bg-purple-100 p-2 rounded-full mr-4 mt-1">
                                        <span className="text-purple-600 font-bold">🌍</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold mb-2">Global Perspective</h4>
                                        <p className="text-gray-700">Work with international clients and understand global software development practices.</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="bg-orange-100 p-2 rounded-full mr-4 mt-1">
                                        <span className="text-orange-600 font-bold">🚀</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold mb-2">Career Acceleration</h4>
                                        <p className="text-gray-700">Fast-track your career with real project experience and potential full-time opportunities.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Get Notified Section */}
            <section className="py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Be the First to Know</h2>
                        <p className="text-lg text-gray-600 mb-8">
                            While our internship program is still in development, we'd love to connect with students and recent graduates who are interested in joining us when we launch.
                        </p>

                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-xl mb-8">
                            <h3 className="text-xl font-bold mb-4">What to Expect When We Launch:</h3>
                            <div className="grid md:grid-cols-2 gap-6 text-left">
                                <div>
                                    <h4 className="font-semibold mb-2">🎓 For Students:</h4>
                                    <ul className="text-gray-700 space-y-1 text-sm">
                                        <li>• Final year engineering students</li>
                                        <li>• Computer Science/IT background preferred</li>
                                        <li>• Passion for technology and learning</li>
                                        <li>• Good communication skills</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="font-semibold mb-2">📚 For Recent Graduates:</h4>
                                    <ul className="text-gray-700 space-y-1 text-sm">
                                        <li>• 0-1 years of experience</li>
                                        <li>• Looking to start career in tech</li>
                                        <li>• Eager to learn and grow</li>
                                        <li>• Open to mentorship and guidance</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => setContactFormOpen(true)}
                                className="bg-indigo-600 text-white font-bold py-4 px-8 rounded-md hover:bg-indigo-700 transition-colors inline-flex items-center justify-center group"
                            >
                                EXPRESS INTEREST
                                {/* <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" /> */}
                            </button>
                            <button className="border-2 border-indigo-600 text-indigo-600 font-bold py-4 px-8 rounded-md hover:bg-indigo-600 hover:text-white transition-colors">
                                Learn About Our Culture
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Timeline Section */}
            {/* <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Expected Timeline</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Here's our projected timeline for launching the internship program.
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid md:grid-cols-4 gap-8">
                            <div className="text-center">
                                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-blue-600 font-bold">Q2</span>
                                </div>
                                <h3 className="font-bold mb-2">2025 Q2</h3>
                                <p className="text-gray-600 text-sm">Program design and curriculum development</p>
                            </div>

                            <div className="text-center">
                                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-green-600 font-bold">Q3</span>
                                </div>
                                <h3 className="font-bold mb-2">2025 Q3</h3>
                                <p className="text-gray-600 text-sm">Mentor training and infrastructure setup</p>
                            </div>

                            <div className="text-center">
                                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-purple-600 font-bold">Q4</span>
                                </div>
                                <h3 className="font-bold mb-2">2025 Q4</h3>
                                <p className="text-gray-600 text-sm">Application process and candidate selection</p>
                            </div>

                            <div className="text-center">
                                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-orange-600 font-bold">Q1</span>
                                </div>
                                <h3 className="font-bold mb-2">2026 Q1</h3>
                                <p className="text-gray-600 text-sm">Program launch with first cohort</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-indigo-900 to-purple-800 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Shape Your Future?</h2>
                    <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                        Join us in building the next generation of world-class software professionals. Express your interest today and be part of our journey from the beginning.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => setContactFormOpen(true)}
                            className="bg-yellow-400 text-black font-bold py-4 px-8 rounded-md hover:bg-yellow-300 transition-colors inline-flex items-center justify-center group"
                        >
                            GET EARLY ACCESS
                            <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                        </button>
                        {/* <button className="border-2 border-white text-white font-bold py-4 px-8 rounded-md hover:bg-white hover:text-indigo-900 transition-colors">
                            Download Program Overview
                        </button> */}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InternshipProgramPage;